/**
 * `/$org/apps/probe/briefing`: what the examiner has read before it walks
 * in, the same facts it reads, so the person knows where the questions come
 * from. One line of properties (who is examined, the regulators the examiner
 * speaks for, the rehearsals it remembers), then the file as bordered lists
 * under an icon each: open findings, regulator changes, what each authority
 * expects and the policy library, five rows apiece until Show all, every row
 * on one grid, each finding and policy the server named by id opening its
 * record.
 *
 * @module components/apps/probe/ProbeBriefingPage
 */
import type { ProbeBriefing, ProbeBriefingFile } from "@amoreg/contracts";
import { Link } from "@tanstack/react-router";
import {
  FilesIcon,
  FlagIcon,
  HistoryIcon,
  LandmarkIcon,
  ListChecksIcon,
  PhoneIcon,
  RotateCwIcon,
  ScaleIcon,
  ScrollTextIcon,
  UserRoundIcon,
} from "lucide-react";
import { useMemo } from "react";

import { externalHrefHost, safeExternalHref } from "~/lib/safeExternalHref";
import { cn } from "~/lib/utils";
import { PageHeader } from "../../regulation/PageHeader";
import { RESTRICTED_PROVIDERS_COPY } from "../../regulation/probe/probeRefusal";
import { Button } from "../../ui/button";
import { Skeleton } from "../../ui/skeleton";
import { briefingItems, libraryFacts, nameFromEmail, type LibraryFacts } from "./probeArea";
import { useProbeArea } from "./ProbeAreaProvider";
import {
  PROBE_COLUMN,
  PROBE_LIST_LINK,
  PROBE_LIST_ROW,
  ProbeList,
  ProbeLoadError,
  ProbeProperty,
  QUIET,
  SeverityGlyph,
  probeDay,
  useProbeCap,
} from "./ProbeParts";

/** A list's rows on one grid: a 16px lead, the text, then right-aligned columns. */
const ROW = cn("grid items-start gap-x-3", PROBE_LIST_ROW);
const ROW_3 = "grid-cols-[1rem_minmax(0,1fr)_auto]";
/** A change: its title, then where it was published and when, each in a column of its own; the source goes when narrow. */
const CHANGE_ROW = "grid-cols-[1rem_minmax(0,1fr)_7rem_6rem] @max-xl/briefing:grid-cols-[1rem_minmax(0,1fr)_6rem]";
/** A document: its kind, title, status, owner and review date; the owner goes when narrow. */
const LIBRARY_ROW =
  "grid-cols-[1rem_minmax(0,1fr)_6.5rem_8rem_6rem] @max-2xl/briefing:grid-cols-[1rem_minmax(0,1fr)_6.5rem_6rem]";
/** A list in a quiet bordered box. */
const BOX = "overflow-hidden rounded-lg border border-border/70";
const SECTION_ICON = cn("size-3.5", QUIET);

export function ProbeBriefingPage() {
  const { briefing, briefingStatus, retryBriefing, orgSegment, call } = useProbeArea();

  return (
    <>
      <PageHeader
        title="Briefing"
        actions={
          <>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Read the briefing again"
              onClick={retryBriefing}
              disabled={briefingStatus === "loading"}
              className="text-muted-foreground"
            >
              <RotateCwIcon
                className={cn("size-3.5", briefingStatus === "loading" && "animate-spin")}
              />
            </Button>
            <Button size="sm" render={<Link to="/$org/apps/probe" params={{ org: orgSegment }} />}>
              <PhoneIcon className="size-3.5" />
              Start interview
            </Button>
          </>
        }
      />
      <div className="min-h-0 flex-1 overflow-y-auto" data-probe-briefing="">
        {briefing === null && briefingStatus === "loading" ? (
          <BriefingSkeleton />
        ) : briefing === null && briefingStatus === "restricted" ? (
          <p
            role="alert"
            className="mx-auto max-w-md px-6 py-16 text-center text-[13px] text-muted-foreground"
            data-probe-restricted=""
          >
            {RESTRICTED_PROVIDERS_COPY}
          </p>
        ) : briefing === null ? (
          <ProbeLoadError what="The briefing" onRetry={retryBriefing} />
        ) : (
          <div className={cn(PROBE_COLUMN, "@container/briefing flex flex-col gap-5 pt-5 pb-10")}>
            <Properties briefing={briefing} />
            {briefing.file === undefined ? (
              <p className={cn("px-2 text-[13px]", QUIET)}>
                This server doesn&rsquo;t share the examiner&rsquo;s file yet.
              </p>
            ) : (
              <FileSections
                file={briefing.file}
                orgSegment={orgSegment}
                // Leaving Probe ends the call, so beside one a record opens in a new tab.
                openBesideCall={call.active || call.state.phase === "connecting"}
                showJurisdiction={briefing.jurisdictions.length > 1}
              />
            )}
          </div>
        )}
      </div>
    </>
  );
}

/** Who is examined, by which regulators, and how many rehearsals the examiner remembers, on one line. */
function Properties({ briefing }: { readonly briefing: ProbeBriefing }) {
  const { person, jurisdictions, rehearsals } = briefing;
  return (
    <div
      role="group"
      aria-label="Properties"
      className="-ms-2 flex flex-wrap items-center gap-x-1 gap-y-1.5 text-[13px]"
      data-probe-briefing-properties=""
    >
      <ProbeProperty icon={<UserRoundIcon />} title="Examined">
        {person.name ?? person.email}
        {person.jobTitle ? <span className={cn("ms-1.5", QUIET)}>{person.jobTitle}</span> : null}
      </ProbeProperty>
      <ProbeProperty icon={<LandmarkIcon />} title="The examiner speaks for">
        {jurisdictions.length === 0
          ? "No jurisdiction on record"
          : jurisdictions.map((jurisdiction) => jurisdiction.name).join(", ")}
      </ProbeProperty>
      <ProbeProperty icon={<HistoryIcon />} title="Rehearsals the examiner remembers">
        {rehearsals === 1 ? "1 rehearsal" : `${rehearsals} rehearsals`}
      </ProbeProperty>
    </div>
  );
}

function FileSections({
  file,
  orgSegment,
  openBesideCall,
  showJurisdiction,
}: {
  readonly file: ProbeBriefingFile;
  readonly orgSegment: string;
  readonly openBesideCall: boolean;
  /** More than one regulator: each change and passage names its own. */
  readonly showJurisdiction: boolean;
}) {
  const opens = openBesideCall ? { target: "_blank" } : {};
  const items = useMemo(() => briefingItems(file), [file]);
  const findings = useProbeCap(items.findings);
  const updates = useProbeCap(items.updates);
  const authority = useProbeCap(items.authority);
  const library = useProbeCap(items.library);
  return (
    <>
      <div className={BOX}>
        <ProbeList
          id="probe-briefing-findings"
          icon={<FlagIcon aria-hidden className={SECTION_ICON} />}
          title="Open findings"
          count={items.findings.length}
          more={findings.more}
          rows={findings.shown.map((finding, index) => {
            const content = (
              <>
                <span className="flex h-5 items-center">
                  <SeverityGlyph severity={finding.severity ?? ""} />
                </span>
                <span className="min-w-0">
                  <span className="block text-foreground">{finding.title}</span>
                  {finding.summary ? (
                    <span className={cn("block text-pretty", QUIET)}>{finding.summary}</span>
                  ) : null}
                </span>
                <span />
              </>
            );
            return finding.id === null ? (
              <li key={`${index}:${finding.title}`} className={cn(ROW, ROW_3)}>
                {content}
              </li>
            ) : (
              <li key={finding.id}>
                <Link
                  to="/$org/compliance/findings/$findingId"
                  params={{ org: orgSegment, findingId: finding.id }}
                  {...opens}
                  data-probe-finding={finding.id}
                  className={cn(ROW, ROW_3, PROBE_LIST_LINK)}
                >
                  {content}
                </Link>
              </li>
            );
          })}
        />
      </div>

      <div className={BOX}>
        <ProbeList
          id="probe-briefing-changes"
          icon={<LandmarkIcon aria-hidden className={SECTION_ICON} />}
          title="Regulator changes"
          count={items.updates.length}
          more={updates.more}
          rows={updates.shown.map((update, index) => {
            const href = safeExternalHref(update.url);
            const host = externalHrefHost(update.url);
            return (
              <li key={update.id ?? `${index}:${update.title}`} className={cn(ROW, CHANGE_ROW)} data-probe-update="">
                <span />
                <span className="min-w-0 text-pretty text-foreground">
                  {update.title}
                  {showJurisdiction && update.jurisdiction ? (
                    <span className={cn("ms-2", QUIET)}>{update.jurisdiction}</span>
                  ) : null}
                </span>
                <span className="min-w-0 truncate text-end text-xs @max-xl/briefing:hidden">
                  {href === null || host === null ? null : (
                    <a
                      href={href}
                      target="_blank"
                      rel="noreferrer"
                      className={cn("rounded-sm underline-offset-2 hover:text-foreground hover:underline", QUIET)}
                    >
                      {host}
                    </a>
                  )}
                </span>
                <span className={cn("text-end text-xs tabular-nums", QUIET)}>
                  {update.date === null ? "" : probeDay(update.date)}
                </span>
              </li>
            );
          })}
        />
      </div>

      {items.authority.length > 0 ? (
        <div className={BOX}>
          <ProbeList
            id="probe-briefing-authority"
            icon={<ScaleIcon aria-hidden className={SECTION_ICON} />}
            title="What the authority expects"
            count={items.authority.length}
            more={authority.more}
            rows={authority.shown.map((passage, index) => {
              const citation = [showJurisdiction ? passage.jurisdiction : null, passage.source]
                .filter(Boolean)
                .join(", ");
              return (
                <li key={`${index}:${passage.jurisdiction}`} className={cn(ROW, ROW_3)} data-probe-passage="">
                  <span />
                  <span className="min-w-0">
                    {citation === "" ? null : <span className={cn("block text-xs", QUIET)}>{citation}</span>}
                    <span className="block text-pretty text-foreground">{passage.text}</span>
                  </span>
                  <span />
                </li>
              );
            })}
          />
        </div>
      ) : null}

      <div className={BOX}>
        <ProbeList
          id="probe-briefing-library"
          icon={<FilesIcon aria-hidden className={SECTION_ICON} />}
          title="Policy library"
          count={
            file.libraryTotal > items.library.length
              ? `${items.library.length} of ${file.libraryTotal}`
              : items.library.length
          }
          more={library.more}
          rows={library.shown.map((entry, index) => {
            const content = <LibraryCells name={entry.name} facts={libraryFacts(entry.facts)} />;
            return entry.id === null ? (
              <li key={`${index}:${entry.name}`} className={cn(ROW, LIBRARY_ROW)} data-probe-library-entry="">
                {content}
              </li>
            ) : (
              <li key={entry.id} data-probe-library-entry="">
                <Link
                  to="/$org/compliance/policies/$policyId"
                  params={{ org: orgSegment, policyId: entry.id }}
                  {...opens}
                  data-probe-policy={entry.id}
                  className={cn(ROW, LIBRARY_ROW, PROBE_LIST_LINK)}
                >
                  {content}
                </Link>
              </li>
            );
          })}
        />
      </div>
    </>
  );
}

const KIND_ICON = { policy: ScrollTextIcon, procedure: ListChecksIcon } as const;

/** A document's kind, title, status, owner and review date, in the library's columns. */
function LibraryCells({ name, facts }: { readonly name: string; readonly facts: LibraryFacts }) {
  const Kind = facts.kind === "procedure" ? KIND_ICON.procedure : KIND_ICON.policy;
  const kind = facts.kind ?? "policy";
  const ownerName = facts.owner === null ? null : nameFromEmail(facts.owner);
  return (
    <>
      <span className="flex h-5 items-center" title={sentence(kind)}>
        <Kind aria-hidden className={cn("size-3.5", QUIET)} />
        <span className="sr-only">{sentence(kind)}</span>
      </span>
      <span className="min-w-0 text-pretty text-foreground">{name}</span>
      <span className="flex h-5 min-w-0 items-center">
        {facts.status === null ? null : (
          <span
            className={cn(
              "truncate rounded-full border border-border/80 px-1.5 text-[11px] leading-4",
              facts.status === "not under governance" ? "text-warning-foreground" : QUIET,
            )}
          >
            {facts.status === "not under governance" ? "Not governed" : sentence(facts.status.replaceAll("_", " "))}
          </span>
        )}
      </span>
      <span
        className={cn("min-w-0 truncate text-xs leading-5 @max-2xl/briefing:hidden", ownerName === null && QUIET)}
        title={facts.owner ?? undefined}
      >
        {facts.owner === null ? <span className={QUIET}>No owner</span> : (ownerName ?? facts.owner)}
      </span>
      <span className={cn("text-end text-xs leading-5 tabular-nums", QUIET)} title="Review due">
        {facts.reviewDue === null ? "" : probeDay(facts.reviewDue)}
      </span>
    </>
  );
}

const sentence = (text: string): string => text.charAt(0).toUpperCase() + text.slice(1);

function BriefingSkeleton() {
  return (
    <div className={cn(PROBE_COLUMN, "flex flex-col gap-7 pt-5")} aria-busy="true">
      <Skeleton className="h-7 w-96 max-w-full" />
      {Array.from({ length: 3 }, (_, index) => (
        <div key={index} className="flex flex-col gap-2">
          <Skeleton className="h-3.5 w-36" />
          <Skeleton className="h-24" />
        </div>
      ))}
    </div>
  );
}
